import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, clearToken } from '../lib/api';
import DashboardLayout from '../components/DashboardLayout';
import type { ProjectResponse, BranchResponse, ResolvedTranslation, BranchDiffEntry, AiKeyResponse, TranslationJobResponse, GlossaryViolation } from '@quicktl/shared';

type Tab = 'branches' | 'translations' | 'jobs' | 'settings';

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectResponse | null>(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<Tab>('branches');

  async function loadProject() {
    try {
      setProject(await api.getProject(id!));
    } catch (e: any) {
      if (e.status === 401) { clearToken(); navigate('/login'); return; }
      setError(e.body?.error || 'Failed to load project');
    }
  }

  useEffect(() => { loadProject(); }, [id]);

  if (!project && !error) return (
    <DashboardLayout>
      <p className="text-slate-500 text-sm" aria-live="polite">Loading…</p>
    </DashboardLayout>
  );

  const tabs: { key: Tab; label: string }[] = [
    { key: 'branches', label: 'Branches' },
    { key: 'translations', label: 'Translations' },
    { key: 'jobs', label: 'Jobs' },
    { key: 'settings', label: 'Settings' },
  ];

  return (
    <DashboardLayout>
      <nav aria-label="Breadcrumb">
        <button onClick={() => navigate('/')} className="text-sm text-slate-500 hover:text-slate-300 mb-4 block">
          ← Projects
        </button>
      </nav>

      {error && (
        <div role="alert" className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>
      )}

      {project && (
        <>
          <div className="mb-6">
            <h2 className="text-xl font-semibold">{project.name}</h2>
            <p className="text-sm text-slate-500 mt-1">
              Source: {project.sourceLocale} · Targets: {project.targetLocales.join(', ') || '—'} · Branch: {project.defaultBranch}
            </p>
          </div>

          <div role="tablist" aria-label="Project sections" className="flex gap-1 mb-6 border-b border-slate-800">
            {tabs.map(t => (
              <button
                key={t.key}
                role="tab"
                aria-selected={tab === t.key}
                aria-controls={`tabpanel-${t.key}`}
                id={`tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.key
                    ? 'border-emerald-500 text-emerald-400'
                    : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div role="tabpanel" id={`tabpanel-${tab}`} aria-labelledby={`tab-${tab}`}>
            {tab === 'branches' && <BranchesTab projectId={project.id} defaultBranch={project.defaultBranch} />}
            {tab === 'translations' && <TranslationsTab project={project} />}
            {tab === 'jobs' && <JobsTab projectId={project.id} />}
            {tab === 'settings' && <SettingsTab project={project} onUpdate={setProject} onDelete={() => navigate('/')} />}
          </div>
        </>
      )}
    </DashboardLayout>
  );
}

// ── Branches Tab ──

function BranchesTab({ projectId, defaultBranch }: { projectId: string; defaultBranch: string }) {
  const [branches, setBranches] = useState<BranchResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newBranch, setNewBranch] = useState('');
  const [creating, setCreating] = useState(false);
  const [comparing, setComparing] = useState<{ source: string; target: string } | null>(null);
  const [diff, setDiff] = useState<BranchDiffEntry[]>([]);
  const [diffLoading, setDiffLoading] = useState(false);

  async function load() {
    try {
      setBranches((await api.listBranches(projectId)).data);
    } catch (e: any) {
      setError(e.body?.error || 'Failed to load branches');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [projectId]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newBranch.trim()) return;
    setCreating(true);
    setError('');
    try {
      const branch = await api.createBranch(projectId, { name: newBranch.trim() });
      setBranches([...branches, branch]);
      setNewBranch('');
    } catch (e: any) {
      setError(e.body?.error || 'Failed to create branch');
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(name: string) {
    if (!confirm(`Delete branch "${name}"?`)) return;
    try {
      await api.deleteBranch(projectId, name);
      setBranches(branches.filter(b => b.name !== name));
    } catch (e: any) {
      setError(e.body?.error || 'Failed to delete branch');
    }
  }

  async function handleMerge(name: string) {
    if (!confirm(`Merge "${name}" into "${defaultBranch}"?`)) return;
    try {
      const result = await api.mergeBranch(projectId, name);
      if (result.conflicts.length > 0) {
        setError(`Merge had ${result.conflicts.length} conflict(s): ${result.conflicts.map(c => c.key).join(', ')}`);
      } else {
        setBranches(branches.filter(b => b.name !== name));
      }
    } catch (e: any) {
      setError(e.body?.error || 'Failed to merge branch');
    }
  }

  async function handleCompare(source: string) {
    const target = defaultBranch;
    setComparing({ source, target });
    setDiffLoading(true);
    setDiff([]);
    try {
      setDiff(await api.compareBranches(projectId, source, target));
    } catch (e: any) {
      setError(e.body?.error || 'Failed to compare branches');
      setComparing(null);
    } finally {
      setDiffLoading(false);
    }
  }

  return (
    <div>
      {error && <div role="alert" className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}

      <form onSubmit={handleCreate} className="flex gap-2 mb-4">
        <label htmlFor="new-branch-name" className="sr-only">New branch name</label>
        <input
          id="new-branch-name"
          type="text"
          placeholder="New branch name"
          value={newBranch}
          onChange={e => setNewBranch(e.target.value)}
          className="flex-1 px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
        <button
          type="submit"
          disabled={creating}
          className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          {creating ? 'Creating…' : 'Create'}
        </button>
      </form>

      {loading ? (
        <p className="text-slate-500 text-sm" aria-live="polite">Loading…</p>
      ) : branches.length === 0 ? (
        <p className="text-slate-500 text-sm">No branches.</p>
      ) : (
        <div className="space-y-2">
          {branches.map(b => (
            <div key={b.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-800">
              <div>
                <span className="text-sm font-medium">{b.name}</span>
                {b.name === defaultBranch && (
                  <span className="ml-2 text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">default</span>
                )}
                <span className="ml-2 text-xs text-slate-600">{b.status}</span>
              </div>
              {b.name !== defaultBranch && (
                <div className="flex gap-2">
                  <button onClick={() => handleCompare(b.name)} aria-label={`Compare branch ${b.name} with ${defaultBranch}`} className="text-xs text-slate-500 hover:text-emerald-400 transition-colors">
                    Compare
                  </button>
                  <button onClick={() => handleMerge(b.name)} aria-label={`Merge branch ${b.name}`} className="text-xs text-slate-500 hover:text-emerald-400 transition-colors">
                    Merge
                  </button>
                  <button onClick={() => handleDelete(b.name)} aria-label={`Delete branch ${b.name}`} className="text-xs text-slate-500 hover:text-red-400 transition-colors">
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {comparing && (
        <div className="mt-6 border-t border-slate-800 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium">
              Comparing <span className="text-emerald-400">{comparing.source}</span> → <span className="text-emerald-400">{comparing.target}</span>
            </h3>
            <button onClick={() => setComparing(null)} aria-label="Close comparison" className="text-xs text-slate-500 hover:text-slate-300">
              Close
            </button>
          </div>

          <div aria-live="polite">
            {diffLoading ? (
              <p className="text-slate-500 text-sm">Loading…</p>
            ) : diff.length === 0 ? (
              <p className="text-slate-500 text-sm">No differences.</p>
            ) : (
              <div className="space-y-1">
                {diff.map((d, i) => (
                  <div key={i} className="p-2 rounded-lg border border-slate-800 text-sm">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        d.type === 'added' ? 'bg-green-500/20 text-green-400' :
                        d.type === 'removed' ? 'bg-red-500/20 text-red-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>{d.type}</span>
                      <span className="font-mono text-xs text-slate-400">{d.key}</span>
                      <span className="text-xs text-slate-600">[{d.locale}]</span>
                    </div>
                    {d.type === 'modified' && (
                      <div className="mt-1 ml-4 text-xs space-y-0.5">
                        <div className="text-red-400/70"><span className="text-slate-600">{comparing.target}:</span> {d.oldValue}</div>
                        <div className="text-green-400/70"><span className="text-slate-600">{comparing.source}:</span> {d.newValue}</div>
                      </div>
                    )}
                    {d.type === 'added' && d.newValue && (
                      <div className="mt-1 ml-4 text-xs text-green-400/70">{d.newValue}</div>
                    )}
                    {d.type === 'removed' && d.oldValue && (
                      <div className="mt-1 ml-4 text-xs text-red-400/70">{d.oldValue}</div>
                    )}
                  </div>
                ))}
                <p className="text-xs text-slate-600 mt-2">
                  {diff.filter(d => d.type === 'added').length} added, {diff.filter(d => d.type === 'removed').length} removed, {diff.filter(d => d.type === 'modified').length} modified
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Translations Tab ──

function TranslationsTab({ project }: { project: ProjectResponse }) {
  const [branch, setBranch] = useState(project.defaultBranch);
  const [locale, setLocale] = useState(project.sourceLocale);
  const [translations, setTranslations] = useState<ResolvedTranslation[]>([]);
  const [branches, setBranches] = useState<BranchResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const allLocales = [project.sourceLocale, ...project.targetLocales];

  useEffect(() => {
    api.listBranches(project.id).then(r => setBranches(r.data)).catch(() => {});
  }, [project.id]);

  async function loadTranslations() {
    setLoading(true);
    setError('');
    try {
      setTranslations((await api.getTranslations(project.id, branch, locale)).data);
    } catch (e: any) {
      setError(e.body?.error || 'Failed to load translations');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadTranslations(); }, [project.id, branch, locale]);

  async function handleSave(key: string) {
    try {
      await api.updateTranslation(project.id, branch, locale, key, { value: editValue });
      setTranslations(translations.map(t => t.key === key ? { ...t, value: editValue } : t));
      setEditing(null);
    } catch (e: any) {
      setError(e.body?.error || 'Failed to update translation');
    }
  }

  async function handleDeleteKey(key: string) {
    if (!confirm(`Delete key "${key}" for locale "${locale}"?`)) return;
    try {
      await api.deleteTranslation(project.id, branch, locale, key);
      setTranslations(translations.filter(t => t.key !== key));
    } catch (e: any) {
      setError(e.body?.error || 'Failed to delete translation');
    }
  }

  return (
    <div>
      {error && <div role="alert" className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}

      <div className="flex gap-3 mb-4">
        <div>
          <label htmlFor="translations-branch" className="sr-only">Branch</label>
          <select
            id="translations-branch"
            value={branch}
            onChange={e => setBranch(e.target.value)}
            className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {branches.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="translations-locale" className="sr-only">Locale</label>
          <select
            id="translations-locale"
            value={locale}
            onChange={e => setLocale(e.target.value)}
            className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {allLocales.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
      </div>

      <div aria-live="polite">
        {loading ? (
          <p className="text-slate-500 text-sm">Loading…</p>
        ) : translations.length === 0 ? (
          <p className="text-slate-500 text-sm">No translations for this branch/locale.</p>
        ) : (
          <div className="space-y-1">
            {translations.map(t => (
              <div key={t.key} className="flex items-start gap-3 p-3 rounded-lg border border-slate-800 text-sm">
                <div className="w-1/3 shrink-0">
                  <span className="font-mono text-xs text-slate-400 break-all">{t.key}</span>
                </div>
                <div className="flex-1 min-w-0">
                  {editing === t.key ? (
                    <div className="flex gap-2">
                      <label htmlFor={`edit-${t.key}`} className="sr-only">Value for {t.key}</label>
                      <input
                        id={`edit-${t.key}`}
                        type="text"
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSave(t.key); if (e.key === 'Escape') setEditing(null); }}
                        autoFocus
                        className="flex-1 px-2 py-1 rounded bg-slate-900 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                      <button onClick={() => handleSave(t.key)} aria-label={`Save ${t.key}`} className="text-xs text-emerald-400 hover:text-emerald-300">Save</button>
                      <button onClick={() => setEditing(null)} aria-label="Cancel editing" className="text-xs text-slate-500 hover:text-slate-300">Cancel</button>
                    </div>
                  ) : (
                    <button
                      className="text-left hover:text-white transition-colors break-all"
                      onClick={() => { setEditing(t.key); setEditValue(t.value); }}
                      aria-label={`Edit value for ${t.key}`}
                    >
                      {t.value}
                    </button>
                  )}
                </div>
                <button onClick={() => handleDeleteKey(t.key)} aria-label={`Delete key ${t.key}`} className="text-xs text-slate-600 hover:text-red-400 transition-colors shrink-0">
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Settings Tab ──

function SettingsTab({ project, onUpdate, onDelete }: { project: ProjectResponse; onUpdate: (p: ProjectResponse) => void; onDelete: () => void }) {
  const [name, setName] = useState(project.name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newLocale, setNewLocale] = useState('');
  const [aiKeys, setAiKeys] = useState<AiKeyResponse[]>([]);
  const [selectedAiKey, setSelectedAiKey] = useState(project.aiProviderKeyId || '');
  const [savingAiKey, setSavingAiKey] = useState(false);
  const [aiKeySuccess, setAiKeySuccess] = useState('');
  const [projectContext, setProjectContext] = useState(project.projectContext || '');
  const [savingContext, setSavingContext] = useState(false);
  const [contextSuccess, setContextSuccess] = useState('');

  useEffect(() => {
    api.listAiKeys().then(r => setAiKeys(r.data)).catch(() => {});
  }, []);

  async function handleSave() {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const updated = await api.updateProject(project.id, { name });
      onUpdate(updated);
      setSuccess('Saved');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e: any) {
      setError(e.body?.error || 'Failed to update project');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddLocale(e: React.FormEvent) {
    e.preventDefault();
    if (!newLocale.trim()) return;
    try {
      const result = await api.addLocale(project.id, newLocale.trim());
      onUpdate(result.project);
      setNewLocale('');
    } catch (e: any) {
      setError(e.body?.error || 'Failed to add locale');
    }
  }

  async function handleRemoveLocale(locale: string) {
    if (!confirm(`Remove locale "${locale}"?`)) return;
    try {
      const updated = await api.removeLocale(project.id, locale);
      onUpdate(updated);
    } catch (e: any) {
      setError(e.body?.error || 'Failed to remove locale');
    }
  }

  async function handleDeleteProject() {
    if (!confirm(`Delete project "${project.name}"? This cannot be undone.`)) return;
    try {
      await api.deleteProject(project.id);
      onDelete();
    } catch (e: any) {
      setError(e.body?.error || 'Failed to delete project');
    }
  }

  return (
    <div className="space-y-8">
      {error && <div role="alert" className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}

      <section>
        <label htmlFor="settings-project-name" className="text-sm font-medium mb-3 block">Project name</label>
        <div className="flex gap-2">
          <input
            id="settings-project-name"
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            className="flex-1 px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <button
            onClick={handleSave}
            disabled={saving || name === project.name}
            className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {success && <span className="text-green-400 text-sm self-center" role="status">{success}</span>}
        </div>
      </section>

      <section>
        <label htmlFor="settings-ai-key" className="text-sm font-medium mb-3 block">AI key</label>
        <div className="flex gap-2">
          <select
            id="settings-ai-key"
            value={selectedAiKey}
            onChange={e => setSelectedAiKey(e.target.value)}
            className="flex-1 px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="">None</option>
            {aiKeys.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
          <button
            onClick={async () => {
              setSavingAiKey(true);
              setError('');
              try {
                const updated = await api.updateProject(project.id, { aiProviderKeyId: selectedAiKey || null });
                onUpdate(updated);
                setAiKeySuccess('Saved');
                setTimeout(() => setAiKeySuccess(''), 2000);
              } catch (e: any) {
                setError(e.body?.error || 'Failed to update AI key');
              } finally {
                setSavingAiKey(false);
              }
            }}
            disabled={savingAiKey || selectedAiKey === (project.aiProviderKeyId || '')}
            className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            {savingAiKey ? 'Saving…' : 'Save'}
          </button>
          {aiKeySuccess && <span className="text-green-400 text-sm self-center" role="status">{aiKeySuccess}</span>}
        </div>
        {aiKeys.length === 0 && (
          <p className="text-xs text-slate-500 mt-2">No AI keys configured. Add one in Account settings.</p>
        )}
      </section>

      <section>
        <label htmlFor="settings-project-context" className="text-sm font-medium mb-1 block">Project context</label>
        <p className="text-xs text-slate-500 mb-2">
          Describe what your project is and how translations should be approached. This context is included in AI translation prompts to improve translation quality.
        </p>
        <textarea
          id="settings-project-context"
          rows={4}
          value={projectContext}
          onChange={e => setProjectContext(e.target.value)}
          maxLength={5000}
          placeholder=""
          className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-slate-500">
            {projectContext.trim() ? `≈ ${Math.ceil(projectContext.trim().length / 4)} tokens added to each AI prompt` : 'No extra tokens'}
            {' · '}{projectContext.length}/5,000 chars
          </span>
          <div className="flex items-center gap-2">
            {contextSuccess && <span className="text-green-400 text-sm" role="status">{contextSuccess}</span>}
            <button
              onClick={async () => {
                setSavingContext(true);
                setError('');
                try {
                  const updated = await api.updateProject(project.id, { projectContext: projectContext.trim() || null });
                  onUpdate(updated);
                  setContextSuccess('Saved');
                  setTimeout(() => setContextSuccess(''), 2000);
                } catch (e: any) {
                  setError(e.body?.error || 'Failed to update project context');
                } finally {
                  setSavingContext(false);
                }
              }}
              disabled={savingContext || projectContext === (project.projectContext || '')}
              className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              {savingContext ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-sm font-medium mb-3">Target locales</h3>
        <div className="flex flex-wrap gap-2 mb-3">
          {project.targetLocales.length === 0 ? (
            <p className="text-slate-500 text-sm">No target locales configured.</p>
          ) : (
            project.targetLocales.map(l => (
              <span key={l} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-800 text-sm">
                {l}
                <button onClick={() => handleRemoveLocale(l)} aria-label={`Remove locale ${l}`} className="text-slate-500 hover:text-red-400">×</button>
              </span>
            ))
          )}
        </div>
        <form onSubmit={handleAddLocale} className="flex gap-2">
          <label htmlFor="settings-add-locale" className="sr-only">Add locale</label>
          <input
            id="settings-add-locale"
            type="text"
            placeholder="Add locale (e.g. fr, de, ja)"
            value={newLocale}
            onChange={e => setNewLocale(e.target.value)}
            className="flex-1 px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <button
            type="submit"
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            Add
          </button>
        </form>
      </section>

      <section aria-label="Project information">
        <h3 className="text-sm font-medium mb-1">Info</h3>
        <dl className="text-sm text-slate-400 space-y-1">
          <div><dt className="inline text-slate-500">ID:</dt> <dd className="inline font-mono text-xs">{project.id}</dd></div>
          <div><dt className="inline text-slate-500">Source locale:</dt> <dd className="inline">{project.sourceLocale}</dd></div>
          <div><dt className="inline text-slate-500">Default branch:</dt> <dd className="inline">{project.defaultBranch}</dd></div>
          <div><dt className="inline text-slate-500">Created:</dt> <dd className="inline">{new Date(project.createdAt).toLocaleDateString()}</dd></div>
        </dl>
      </section>

      <section className="border-t border-slate-800 pt-6" aria-label="Danger zone">
        <h3 className="text-sm font-medium text-red-400 mb-2">Danger zone</h3>
        <button
          onClick={handleDeleteProject}
          className="bg-red-600 hover:bg-red-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          Delete project
        </button>
      </section>
    </div>
  );
}

// ── Jobs Tab ──

function JobsTab({ projectId }: { projectId: string }) {
  const [jobs, setJobs] = useState<TranslationJobResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  async function load() {
    try {
      setLoading(true);
      setError('');
      const { data } = await api.listTranslationJobs(projectId, { page: 1, pageSize: 20 });
      setJobs(data);
    } catch (e: any) {
      setError(e.body?.error || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [projectId]);

  function toggle(id: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  if (loading) return <p className="text-slate-500 text-sm" aria-live="polite">Loading…</p>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-400">Most recent 20 translation jobs.</p>
        <button
          onClick={load}
          className="text-xs px-3 py-1.5 rounded-md border border-slate-700 text-slate-300 hover:border-slate-600 hover:text-slate-100 transition-colors"
        >
          Refresh
        </button>
      </div>
      {error && (
        <div role="alert" className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>
      )}
      {jobs.length === 0 ? (
        <p className="text-slate-500 text-sm">No translation jobs yet for this project.</p>
      ) : (
        <ul className="space-y-2">
          {jobs.map(job => (
            <JobRow key={job.id} job={job} expanded={expanded.has(job.id)} onToggle={() => toggle(job.id)} />
          ))}
        </ul>
      )}
    </div>
  );
}

function JobRow({ job, expanded, onToggle }: { job: TranslationJobResponse; expanded: boolean; onToggle: () => void }) {
  const totals = job.result
    ? Object.values(job.result).reduce(
        (acc, loc) => ({
          translated: acc.translated + loc.translated,
          fromMemory: acc.fromMemory + loc.fromMemory,
          violations: acc.violations + loc.violations.length,
          skipped: acc.skipped + (loc.glossaryCheckSkipped ? 1 : 0),
        }),
        { translated: 0, fromMemory: 0, violations: 0, skipped: 0 },
      )
    : null;

  const hasDetail = job.result !== null || job.error !== null;

  return (
    <li className="rounded-lg border border-slate-800 bg-slate-900/40">
      <button
        onClick={onToggle}
        disabled={!hasDetail}
        aria-expanded={expanded}
        className="w-full text-left p-3 flex items-start gap-3 hover:bg-slate-900/80 disabled:cursor-default disabled:hover:bg-transparent transition-colors"
      >
        <JobStatusBadge status={job.status} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-mono text-sm text-slate-200">{job.branchName}</span>
            <span className="text-xs text-slate-500">→ {job.targetLocales.join(', ')}</span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {formatJobTime(job)}
            {totals && totals.violations > 0 && (
              <span className="ml-2 text-amber-400">{totals.violations} glossary violation{totals.violations === 1 ? '' : 's'}</span>
            )}
            {totals && totals.skipped > 0 && (
              <span className="ml-2 text-amber-400">⚠ glossary check skipped ({totals.skipped} locale{totals.skipped === 1 ? '' : 's'})</span>
            )}
          </div>
        </div>
        {hasDetail && (
          <span className="text-slate-500 text-xs mt-1" aria-hidden="true">{expanded ? '▾' : '▸'}</span>
        )}
      </button>
      {expanded && hasDetail && (
        <div className="border-t border-slate-800 p-3 text-sm">
          {job.error && (
            <div role="alert" className="mb-3 p-2 rounded bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-mono whitespace-pre-wrap">
              {job.error}
            </div>
          )}
          {job.result && Object.entries(job.result).map(([locale, loc]) => (
            <div key={locale} className="mb-3 last:mb-0">
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-mono text-slate-200">{locale}</span>
                <span className="text-xs text-slate-500">
                  {loc.translated} translated{loc.fromMemory > 0 ? `, ${loc.fromMemory} from memory` : ''}
                </span>
              </div>
              {loc.glossaryCheckSkipped && (
                <p className="text-xs text-amber-400 mb-1">
                  ⚠ Glossary compliance could not be verified — the model did not report which terms it used. This usually means a model regression; the translations may still be fine.
                </p>
              )}
              {loc.violations.length > 0 && (
                <ul className="space-y-1 mt-1">
                  {loc.violations.map((v: GlossaryViolation, i: number) => (
                    <li key={`${v.key}-${v.sourceTerm}-${i}`} className="text-xs p-2 rounded bg-amber-500/5 border border-amber-500/20">
                      <div className="text-amber-300">
                        <span className="font-mono">{v.key}</span>
                        <span className="text-slate-400"> — missing glossary term </span>
                        <span className="font-mono">{v.sourceTerm}</span>
                        <span className="text-slate-400"> (expected </span>
                        <span className="font-mono">{v.expectedTranslation}</span>
                        <span className="text-slate-400">)</span>
                      </div>
                      <div className="text-slate-400 mt-0.5 font-mono truncate" title={v.actualValue}>
                        got: {v.actualValue}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {loc.violations.length === 0 && !loc.glossaryCheckSkipped && (
                <p className="text-xs text-slate-500">No glossary violations.</p>
              )}
            </div>
          ))}
        </div>
      )}
    </li>
  );
}

function JobStatusBadge({ status }: { status: TranslationJobResponse['status'] }) {
  const styles: Record<TranslationJobResponse['status'], string> = {
    pending: 'bg-slate-500/10 border-slate-500/30 text-slate-400',
    processing: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
    completed: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
    failed: 'bg-red-500/10 border-red-500/30 text-red-400',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${styles[status]} shrink-0 mt-0.5`}>
      {status}
    </span>
  );
}

function formatJobTime(job: TranslationJobResponse): string {
  const created = new Date(job.createdAt);
  const now = new Date();
  const diffMs = now.getTime() - created.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  let rel: string;
  if (diffMin < 1) rel = 'just now';
  else if (diffMin < 60) rel = `${diffMin}m ago`;
  else if (diffHour < 24) rel = `${diffHour}h ago`;
  else if (diffDay < 7) rel = `${diffDay}d ago`;
  else rel = created.toLocaleDateString();

  if (job.status === 'processing' && job.progress) {
    return `${rel} · ${job.progress.completedLocales.length}/${job.progress.totalLocales} locales done`;
  }
  if (job.status === 'completed' && job.startedAt && job.completedAt) {
    const durationMs = new Date(job.completedAt).getTime() - new Date(job.startedAt).getTime();
    const durationSec = Math.round(durationMs / 1000);
    return `${rel} · took ${durationSec}s`;
  }
  return rel;
}
